// ✅ src/pages/coupons/CouponItem.jsx
import React, { useEffect, useState } from "react";
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Save,
  X,
  Ticket,
  Copy,
  Calendar,
  Filter,
  IndianRupee,
  Percent,
  Clock,
  Users,
  User,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Tags,
} from "lucide-react";

import API_BASE_URL from "../../services/api";
import Breadcrumb from "../../components/ui/Breadcrumb";
import Sidebar from "../../components/ui/Sidebar";
import Header from "../../components/ui/Header";
import axiosInstance from "../../utils/axiosInstance";
import { useAuth } from "../../context/AuthContext";
import { canAccess } from "../../utils/permissions";

const CouponItem = () => {
  const { user: currentUser } = useAuth();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);

  const [categories, setCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [formErrors, setFormErrors] = useState({});

  const [filterData, setFilterData] = useState({
    status: "all",
    discountType: "all",
    minOrderAmount: "",
    usageStatus: "all",
    couponType: "all",
    audience: "all",
    categoryScope: "all", // ✅ NEW
  });

  const [formData, setFormData] = useState({
    code: "",
    description: "",
    discountType: "percentage",
    discountValue: "",
    minOrderAmount: "",
    maxDiscount: "",
    usageLimit: "",
    perUserLimit: "",
    validFrom: "",
    validUntil: "",
    autoApply: false,
    isPatientCoupon: false,
    patientId: "",
    isActive: true,
    audience: "ALL",

    // ✅ NEW: Category-wise support
    categoryScope: "ALL", // ALL | INCLUDE | EXCLUDE
    categoryIds: [], // [Int]
  });

  useEffect(() => {
    fetchCoupons();
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`${API_BASE_URL}/api/coupons`);
      setCoupons(response.data || []);
    } catch (err) {
      console.error("Error fetching coupons:", err);
      alert("Failed to load coupons");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      setCatLoading(true);
      // ✅ adjust endpoint if your API is different
      const res = await axiosInstance.get(`${API_BASE_URL}/api/categories`);
      setCategories(res.data || []);
    } catch (err) {
      console.error("Failed to fetch categories", err);
      setCategories([]);
    } finally {
      setCatLoading(false);
    }
  };

  const generateCode = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let code = "";
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData((p) => ({ ...p, code }));
  };

  // ✅ Dates OPTIONAL for ALL general coupons (including NEW/EXISTING audience)
  // Rule: if one date is filled, other date becomes required.
  const validateForm = () => {
    const errors = {};

    if (!formData.code.trim()) errors.code = "Coupon code is required";
    else if (formData.code.trim().length < 3)
      errors.code = "Coupon code must be at least 3 characters";

    if (!formData.discountValue || parseFloat(formData.discountValue) <= 0)
      errors.discountValue = "Discount value must be greater than 0";

    if (
      formData.discountType === "percentage" &&
      parseFloat(formData.discountValue) > 100
    ) {
      errors.discountValue = "Percentage discount cannot exceed 100%";
    }

    if (formData.minOrderAmount && parseFloat(formData.minOrderAmount) < 0) {
      errors.minOrderAmount = "Minimum order amount cannot be negative";
    }

    if (formData.maxDiscount && parseFloat(formData.maxDiscount) < 0) {
      errors.maxDiscount = "Maximum discount cannot be negative";
    }

    if (formData.usageLimit && parseInt(formData.usageLimit, 10) < 0) {
      errors.usageLimit = "Usage limit cannot be negative";
    }

    if (formData.perUserLimit && parseInt(formData.perUserLimit, 10) < 0) {
      errors.perUserLimit = "Per user limit cannot be negative";
    }

    if (formData.isPatientCoupon) {
      if (!formData.patientId || parseInt(formData.patientId, 10) <= 0) {
        errors.patientId = "Patient ID is required for patient specific coupon";
      }
    } else {
      const hasFrom = !!formData.validFrom;
      const hasUntil = !!formData.validUntil;

      if (hasFrom && !hasUntil)
        errors.validUntil = "Valid until is required if valid from is set";
      if (!hasFrom && hasUntil)
        errors.validFrom = "Valid from is required if valid until is set";

      if (hasFrom && hasUntil) {
        if (new Date(formData.validUntil) <= new Date(formData.validFrom)) {
          errors.validUntil = "Valid until must be after valid from";
        }
      }

      // ✅ Category scope validation
      const scope = (formData.categoryScope || "ALL").toUpperCase();
      if (
        (scope === "INCLUDE" || scope === "EXCLUDE") &&
        (!formData.categoryIds || formData.categoryIds.length === 0)
      ) {
        errors.categoryIds = "Please select at least 1 category";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      code: "",
      description: "",
      discountType: "percentage",
      discountValue: "",
      minOrderAmount: "",
      maxDiscount: "",
      usageLimit: "",
      perUserLimit: "",
      validFrom: "",
      validUntil: "",
      autoApply: false,
      isPatientCoupon: false,
      patientId: "",
      isActive: true,
      audience: "ALL",

      // ✅ NEW
      categoryScope: "ALL",
      categoryIds: [],
    });
    setFormErrors({});
    setEditingCoupon(null);
    setShowForm(false);
  };

  const handleEdit = (coupon) => {
    setEditingCoupon(coupon);

    setFormData({
      code: coupon.code || "",
      description: coupon.description || "",
      discountType: coupon.discountType || "percentage",
      discountValue:
        coupon.discountValue !== null && coupon.discountValue !== undefined
          ? String(coupon.discountValue)
          : "",
      minOrderAmount:
        coupon.minOrderAmount !== null && coupon.minOrderAmount !== undefined
          ? String(coupon.minOrderAmount)
          : "",
      maxDiscount:
        coupon.maxDiscount !== null && coupon.maxDiscount !== undefined
          ? String(coupon.maxDiscount)
          : "",
      usageLimit:
        coupon.usageLimit !== null && coupon.usageLimit !== undefined
          ? String(coupon.usageLimit)
          : "",
      perUserLimit:
        coupon.perUserLimit !== null && coupon.perUserLimit !== undefined
          ? String(coupon.perUserLimit)
          : "",
      validFrom: coupon.validFrom ? String(coupon.validFrom).split("T")[0] : "",
      validUntil: coupon.validUntil ? String(coupon.validUntil).split("T")[0] : "",
      autoApply: !!coupon.autoApply,
      isPatientCoupon: !!coupon.isPatientCoupon,
      patientId: coupon.patientId ? String(coupon.patientId) : "",
      isActive: coupon.isActive !== undefined ? !!coupon.isActive : true,
      audience: coupon.audience || "ALL",

      // ✅ NEW: category mapping expects `coupon.categories = [{ categoryId, category: { id,name } }]`
      categoryScope: coupon.categoryScope || "ALL",
      categoryIds: Array.isArray(coupon.categories)
        ? coupon.categories.map((x) => x.categoryId)
        : [],
    });

    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this coupon?")) return;
    try {
      await axiosInstance.delete(`${API_BASE_URL}/api/coupons/${id}`);
      alert("Coupon deleted successfully");
      fetchCoupons();
    } catch (err) {
      console.error("Error deleting coupon:", err);
      alert(err?.response?.data?.error || "Failed to delete coupon");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const payload = {
        code: formData.code.toUpperCase().trim(),
        description: formData.description.trim() || null,
        discountType: formData.discountType,
        discountValue: parseFloat(formData.discountValue),

        minOrderAmount: formData.minOrderAmount
          ? parseFloat(formData.minOrderAmount)
          : null,
        maxDiscount: formData.maxDiscount ? parseFloat(formData.maxDiscount) : null,

        usageLimit: formData.usageLimit ? parseInt(formData.usageLimit, 10) : null,
        perUserLimit: formData.perUserLimit
          ? parseInt(formData.perUserLimit, 10)
          : null,

        autoApply: !!formData.autoApply,
        isPatientCoupon: !!formData.isPatientCoupon,
        patientId:
          formData.isPatientCoupon && formData.patientId
            ? parseInt(formData.patientId, 10)
            : null,
        isActive: !!formData.isActive,

        // ✅ audience allowed only for general coupons
        audience: formData.isPatientCoupon ? "ALL" : formData.audience,

        // ✅ dates optional. Send null if empty.
        validFrom: formData.isPatientCoupon
          ? null
          : formData.validFrom
          ? formData.validFrom
          : null,
        validUntil: formData.isPatientCoupon
          ? null
          : formData.validUntil
          ? formData.validUntil
          : null,

        // ✅ NEW: Category-wise support
        categoryScope: formData.isPatientCoupon
          ? "ALL"
          : (formData.categoryScope || "ALL"),
        categoryIds: formData.isPatientCoupon
          ? []
          : (formData.categoryIds || [])
              .map((x) => parseInt(x, 10))
              .filter((x) => Number.isFinite(x) && x > 0),
      };

      if (editingCoupon) {
        await axiosInstance.put(
          `${API_BASE_URL}/api/coupons/${editingCoupon.id}`,
          payload
        );
        alert("Coupon updated successfully");
      } else {
        await axiosInstance.post(`${API_BASE_URL}/api/coupons`, payload);
        alert("Coupon created successfully");
      }

      resetForm();
      fetchCoupons();
    } catch (err) {
      console.error("Error saving coupon:", err);
      alert(err?.response?.data?.error || "Failed to save coupon");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // ✅ Handles coupons with NO dates (no expiry)
  const getCouponStatus = (coupon) => {
    if (!coupon.isActive) return "inactive";
    if (coupon.isPatientCoupon) return "active";

    const now = new Date();

    if (coupon.validFrom && new Date(coupon.validFrom) > now) return "upcoming";
    if (coupon.validUntil && new Date(coupon.validUntil) < now) return "expired";

    if (coupon.usageLimit && coupon.usedCount >= coupon.usageLimit)
      return "exhausted";

    return "active";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800 border border-green-200";
      case "inactive":
        return "bg-gray-100 text-gray-800 border border-gray-200";
      case "expired":
        return "bg-red-100 text-red-800 border border-red-200";
      case "exhausted":
        return "bg-orange-100 text-orange-800 border border-orange-200";
      case "upcoming":
        return "bg-blue-100 text-blue-800 border border-blue-200";
      default:
        return "bg-gray-100 text-gray-800 border border-gray-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "active":
        return <CheckCircle className="w-3 h-3" />;
      case "inactive":
        return <XCircle className="w-3 h-3" />;
      case "expired":
      case "exhausted":
        return <AlertCircle className="w-3 h-3" />;
      case "upcoming":
        return <Clock className="w-3 h-3" />;
      default:
        return <AlertCircle className="w-3 h-3" />;
    }
  };

  const filteredCoupons = coupons.filter((coupon) => {
    const q = searchTerm.trim().toLowerCase();

    const matchesSearch =
      !q ||
      (coupon.code || "").toLowerCase().includes(q) ||
      (coupon.description || "").toLowerCase().includes(q);

    const status = getCouponStatus(coupon);
    const matchesStatus = filterData.status === "all" || status === filterData.status;

    const matchesDiscountType =
      filterData.discountType === "all" ||
      coupon.discountType === filterData.discountType;

    const matchesMinOrder =
      !filterData.minOrderAmount ||
      (coupon.minOrderAmount || 0) >= parseFloat(filterData.minOrderAmount);

    const matchesCouponType =
      filterData.couponType === "all" ||
      (filterData.couponType === "patient" && coupon.isPatientCoupon) ||
      (filterData.couponType === "general" && !coupon.isPatientCoupon);

    let matchesUsage = true;
    if (filterData.usageStatus === "available") {
      matchesUsage = !(coupon.usageLimit && coupon.usedCount >= coupon.usageLimit);
    } else if (filterData.usageStatus === "exhausted") {
      matchesUsage = coupon.usageLimit && coupon.usedCount >= coupon.usageLimit;
    }

    const matchesAudience =
      filterData.audience === "all" ||
      (coupon.audience || "ALL") === filterData.audience;

    const matchesCategoryScope =
      filterData.categoryScope === "all" ||
      (coupon.categoryScope || "ALL") === filterData.categoryScope;

    return (
      matchesSearch &&
      matchesStatus &&
      matchesDiscountType &&
      matchesMinOrder &&
      matchesCouponType &&
      matchesUsage &&
      matchesAudience &&
      matchesCategoryScope
    );
  });

  const stats = {
    total: coupons.length,
    active: coupons.filter((c) => getCouponStatus(c) === "active").length,
    patient: coupons.filter((c) => c.isPatientCoupon).length,
    general: coupons.filter((c) => !c.isPatientCoupon).length,
    expired: coupons.filter((c) => getCouponStatus(c) === "expired").length,
  };

  const activeFilterCount = Object.values(filterData).filter(
    (v) => v !== "all" && v !== ""
  ).length;

  const handleMenuToggle = () => setSidebarOpen(!sidebarOpen);
  const handleSidebarClose = () => setSidebarOpen(false);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onMenuToggle={handleMenuToggle} isMenuOpen={sidebarOpen} />
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onCollapseToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <main
        className={`pt-16 transition-all duration-300 ${
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
        }`}
      >
        <div className="p-4 md:p-6">
          <Breadcrumb />

          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-semibold text-gray-800">
                  Coupon Management
                </h1>
                <p className="text-xs text-gray-500 mt-1">
                  Manage discount coupons and promotional offers
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    if (!canAccess(currentUser, "canCreate")) return;
                    setShowForm(true);
                    setEditingCoupon(null);
                  }}
                  disabled={!canAccess(currentUser, "canCreate")}
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white text-xs font-medium rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>New Coupon</span>
                </button>

                <button
                  onClick={fetchCoupons}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-xs text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  <span>Refresh</span>
                </button>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <div className="bg-white p-3 rounded-lg border border-gray-200">
              <div className="text-sm font-medium text-gray-500 mb-1">
                Total Coupons
              </div>
              <div className="text-lg font-semibold text-gray-800">
                {stats.total}
              </div>
            </div>
            <div className="bg-white p-3 rounded-lg border border-gray-200">
              <div className="text-sm font-medium text-gray-500 mb-1">Active</div>
              <div className="text-lg font-semibold text-green-600">
                {stats.active}
              </div>
            </div>
            <div className="bg-white p-3 rounded-lg border border-gray-200">
              <div className="text-sm font-medium text-gray-500 mb-1">Patient</div>
              <div className="text-lg font-semibold text-blue-600">
                {stats.patient}
              </div>
            </div>
            <div className="bg-white p-3 rounded-lg border border-gray-200">
              <div className="text-sm font-medium text-gray-500 mb-1">General</div>
              <div className="text-lg font-semibold text-purple-600">
                {stats.general}
              </div>
            </div>
            <div className="bg-white p-3 rounded-lg border border-gray-200">
              <div className="text-sm font-medium text-gray-500 mb-1">Expired</div>
              <div className="text-lg font-semibold text-red-600">
                {stats.expired}
              </div>
            </div>
          </div>

          {/* Search & Filters */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
            <div className="flex flex-col md:flex-row gap-3">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by code or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-xs text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  <Filter className="w-3.5 h-3.5" />
                  <span>Filters</span>
                  {activeFilterCount > 0 && (
                    <span className="bg-blue-600 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {showFilters && (
              <div className="mt-4 p-4 bg-gray-50 rounded-md border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-3">
                  {/* Status */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Status
                    </label>
                    <select
                      value={filterData.status}
                      onChange={(e) =>
                        setFilterData((p) => ({ ...p, status: e.target.value }))
                      }
                      className="w-full px-3 py-2 text-xs border border-gray-300 rounded-md"
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="expired">Expired</option>
                      <option value="exhausted">Exhausted</option>
                      <option value="upcoming">Upcoming</option>
                    </select>
                  </div>

                  {/* Discount Type */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Discount Type
                    </label>
                    <select
                      value={filterData.discountType}
                      onChange={(e) =>
                        setFilterData((p) => ({
                          ...p,
                          discountType: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 text-xs border border-gray-300 rounded-md"
                    >
                      <option value="all">All Types</option>
                      <option value="percentage">Percentage</option>
                      <option value="fixed">Fixed Amount</option>
                    </select>
                  </div>

                  {/* Min Order */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Min Order
                    </label>
                    <div className="relative">
                      <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={filterData.minOrderAmount}
                        onChange={(e) =>
                          setFilterData((p) => ({
                            ...p,
                            minOrderAmount: e.target.value,
                          }))
                        }
                        placeholder="Min amount"
                        className="w-full pl-9 pr-3 py-2 text-xs border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>

                  {/* Usage Status */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Usage Status
                    </label>
                    <select
                      value={filterData.usageStatus}
                      onChange={(e) =>
                        setFilterData((p) => ({
                          ...p,
                          usageStatus: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 text-xs border border-gray-300 rounded-md"
                    >
                      <option value="all">All Usage</option>
                      <option value="available">Available</option>
                      <option value="exhausted">Exhausted</option>
                    </select>
                  </div>

                  {/* Coupon Type */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Coupon Type
                    </label>
                    <select
                      value={filterData.couponType}
                      onChange={(e) =>
                        setFilterData((p) => ({
                          ...p,
                          couponType: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 text-xs border border-gray-300 rounded-md"
                    >
                      <option value="all">All Types</option>
                      <option value="patient">Patient</option>
                      <option value="general">General</option>
                    </select>
                  </div>

                  {/* Audience */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Audience
                    </label>
                    <select
                      value={filterData.audience}
                      onChange={(e) =>
                        setFilterData((p) => ({
                          ...p,
                          audience: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 text-xs border border-gray-300 rounded-md"
                    >
                      <option value="all">All</option>
                      <option value="ALL">All Patients</option>
                      <option value="NEW_PATIENT">New Patients</option>
                      <option value="EXISTING_PATIENT">Existing Patients</option>
                    </select>
                  </div>

                  {/* ✅ Category Scope */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Category Scope
                    </label>
                    <select
                      value={filterData.categoryScope}
                      onChange={(e) =>
                        setFilterData((p) => ({
                          ...p,
                          categoryScope: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 text-xs border border-gray-300 rounded-md"
                    >
                      <option value="all">All</option>
                      <option value="ALL">All Categories</option>
                      <option value="INCLUDE">Only Selected</option>
                      <option value="EXCLUDE">Exclude Selected</option>
                    </select>
                  </div>
                </div>

                {activeFilterCount > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <button
                      onClick={() =>
                        setFilterData({
                          status: "all",
                          discountType: "all",
                          minOrderAmount: "",
                          usageStatus: "all",
                          couponType: "all",
                          audience: "all",
                          categoryScope: "all",
                        })
                      }
                      className="text-xs text-red-600 hover:text-red-700"
                    >
                      Clear all filters
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Form Modal */}
          {showForm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-gray-200 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800">
                        {editingCoupon ? "Edit Coupon" : "Add New Coupon"}
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {editingCoupon
                          ? "Update coupon details"
                          : "Create new discount coupon"}
                      </p>
                    </div>
                    <button
                      onClick={resetForm}
                      className="text-gray-400 hover:text-gray-600 p-1"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="p-4 space-y-4">
                  {/* Code */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Coupon Code *
                    </label>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={formData.code}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              code: e.target.value.toUpperCase(),
                            }))
                          }
                          className={`w-full px-3 py-2 text-xs border rounded-md ${
                            formErrors.code ? "border-red-300" : "border-gray-300"
                          }`}
                          placeholder="SUMMER25"
                        />
                        {formErrors.code && (
                          <p className="text-xs text-red-600 mt-1">
                            {formErrors.code}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={generateCode}
                        className="px-3 py-2 text-xs text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
                      >
                        Generate
                      </button>
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Description
                    </label>
                    <input
                      type="text"
                      value={formData.description}
                      onChange={(e) =>
                        setFormData((p) => ({ ...p, description: e.target.value }))
                      }
                      className="w-full px-3 py-2 text-xs border border-gray-300 rounded-md"
                      placeholder="Optional description"
                    />
                  </div>

                  {/* Discount Type & Value */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        Discount Type
                      </label>
                      <select
                        value={formData.discountType}
                        onChange={(e) =>
                          setFormData((p) => ({ ...p, discountType: e.target.value }))
                        }
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-md"
                      >
                        <option value="percentage">Percentage (%)</option>
                        <option value="fixed">Fixed Amount</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        Discount Value *
                      </label>
                      <div className="relative">
                        {formData.discountType === "percentage" ? (
                          <Percent className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                        ) : (
                          <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                        )}
                        <input
                          type="number"
                          min="0"
                          step={formData.discountType === "percentage" ? "0.01" : "1"}
                          value={formData.discountValue}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              discountValue: e.target.value,
                            }))
                          }
                          className={`w-full pl-9 pr-3 py-2 text-xs border rounded-md ${
                            formErrors.discountValue
                              ? "border-red-300"
                              : "border-gray-300"
                          }`}
                        />
                      </div>
                      {formErrors.discountValue && (
                        <p className="text-xs text-red-600 mt-1">
                          {formErrors.discountValue}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Max Discount & Min Order */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        Max Discount
                      </label>
                      <div className="relative">
                        <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={formData.maxDiscount}
                          onChange={(e) =>
                            setFormData((p) => ({ ...p, maxDiscount: e.target.value }))
                          }
                          className={`w-full pl-9 pr-3 py-2 text-xs border rounded-md ${
                            formErrors.maxDiscount
                              ? "border-red-300"
                              : "border-gray-300"
                          }`}
                          placeholder="Optional"
                        />
                      </div>
                      {formErrors.maxDiscount && (
                        <p className="text-xs text-red-600 mt-1">
                          {formErrors.maxDiscount}
                        </p>
                      )}
                      <p className="text-[10px] text-gray-500 mt-1">
                        Used for percentage coupons
                      </p>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        Min Order
                      </label>
                      <div className="relative">
                        <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.minOrderAmount}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              minOrderAmount: e.target.value,
                            }))
                          }
                          className={`w-full pl-9 pr-3 py-2 text-xs border rounded-md ${
                            formErrors.minOrderAmount
                              ? "border-red-300"
                              : "border-gray-300"
                          }`}
                          placeholder="Optional"
                        />
                      </div>
                      {formErrors.minOrderAmount && (
                        <p className="text-xs text-red-600 mt-1">
                          {formErrors.minOrderAmount}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Usage Limits */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        Usage Limit
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={formData.usageLimit}
                        onChange={(e) =>
                          setFormData((p) => ({ ...p, usageLimit: e.target.value }))
                        }
                        className={`w-full px-3 py-2 text-xs border rounded-md ${
                          formErrors.usageLimit ? "border-red-300" : "border-gray-300"
                        }`}
                        placeholder="∞"
                      />
                      {formErrors.usageLimit && (
                        <p className="text-xs text-red-600 mt-1">
                          {formErrors.usageLimit}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        Per User Limit
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={formData.perUserLimit}
                        onChange={(e) =>
                          setFormData((p) => ({ ...p, perUserLimit: e.target.value }))
                        }
                        className={`w-full px-3 py-2 text-xs border rounded-md ${
                          formErrors.perUserLimit
                            ? "border-red-300"
                            : "border-gray-300"
                        }`}
                        placeholder="∞"
                      />
                      {formErrors.perUserLimit && (
                        <p className="text-xs text-red-600 mt-1">
                          {formErrors.perUserLimit}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Patient Coupon Toggle */}
                  <div className="p-3 bg-gray-50 rounded-md">
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-xs font-medium text-gray-700">
                          Patient Specific Coupon
                        </label>
                        <p className="text-[10px] text-gray-500">
                          No dates required, tied to specific patient
                        </p>
                      </div>

                      <input
                        type="checkbox"
                        id="patient-toggle"
                        checked={formData.isPatientCoupon}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setFormData((p) => ({
                            ...p,
                            isPatientCoupon: checked,
                            validFrom: checked ? "" : p.validFrom,
                            validUntil: checked ? "" : p.validUntil,
                            audience: checked ? "ALL" : p.audience,
                            patientId: checked ? p.patientId : "",
                            categoryScope: checked ? "ALL" : p.categoryScope,
                            categoryIds: checked ? [] : p.categoryIds,
                          }));
                        }}
                        className="sr-only"
                      />

                      <label
                        htmlFor="patient-toggle"
                        className={`relative w-10 h-5 flex items-center rounded-full cursor-pointer ${
                          formData.isPatientCoupon ? "bg-blue-500" : "bg-gray-300"
                        }`}
                      >
                        <span
                          className={`bg-white w-4 h-4 rounded-full shadow transform transition-transform duration-200 ${
                            formData.isPatientCoupon
                              ? "translate-x-6"
                              : "translate-x-0.5"
                          }`}
                        />
                      </label>
                    </div>
                  </div>

                  {/* Patient ID */}
                  {formData.isPatientCoupon && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        Patient ID *
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={formData.patientId}
                        onChange={(e) =>
                          setFormData((p) => ({ ...p, patientId: e.target.value }))
                        }
                        className={`w-full px-3 py-2 text-xs border rounded-md ${
                          formErrors.patientId ? "border-red-300" : "border-gray-300"
                        }`}
                        placeholder="Enter patient id"
                      />
                      {formErrors.patientId && (
                        <p className="text-xs text-red-600 mt-1">
                          {formErrors.patientId}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Dates + Audience + Categories */}
                  {!formData.isPatientCoupon && (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1.5">
                            Valid From (optional)
                          </label>
                          <div className="relative">
                            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                            <input
                              type="date"
                              value={formData.validFrom}
                              onChange={(e) =>
                                setFormData((p) => ({
                                  ...p,
                                  validFrom: e.target.value,
                                }))
                              }
                              className={`w-full pl-9 pr-3 py-2 text-xs border rounded-md ${
                                formErrors.validFrom
                                  ? "border-red-300"
                                  : "border-gray-300"
                              }`}
                            />
                          </div>
                          {formErrors.validFrom && (
                            <p className="text-xs text-red-600 mt-1">
                              {formErrors.validFrom}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1.5">
                            Valid Until (optional)
                          </label>
                          <div className="relative">
                            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                            <input
                              type="date"
                              value={formData.validUntil}
                              onChange={(e) =>
                                setFormData((p) => ({
                                  ...p,
                                  validUntil: e.target.value,
                                }))
                              }
                              className={`w-full pl-9 pr-3 py-2 text-xs border rounded-md ${
                                formErrors.validUntil
                                  ? "border-red-300"
                                  : "border-gray-300"
                              }`}
                            />
                          </div>
                          {formErrors.validUntil && (
                            <p className="text-xs text-red-600 mt-1">
                              {formErrors.validUntil}
                            </p>
                          )}
                        </div>
                      </div>

                      <p className="text-[10px] text-gray-500 -mt-2">
                        Leave both empty for <b>no expiry</b>. If you fill one date,
                        you must fill the other.
                      </p>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">
                          Coupon Audience
                        </label>
                        <select
                          value={formData.audience}
                          onChange={(e) =>
                            setFormData((p) => ({ ...p, audience: e.target.value }))
                          }
                          className="w-full px-3 py-2 text-xs border border-gray-300 rounded-md"
                        >
                          <option value="ALL">All Patients</option>
                          <option value="NEW_PATIENT">New Patients Only</option>
                          <option value="EXISTING_PATIENT">
                            Existing Patients Only
                          </option>
                        </select>
                        <p className="text-[10px] text-gray-500 mt-1">
                          New/Existing is decided by whether the patient has previous
                          orders.
                        </p>
                      </div>

                      {/* ✅ NEW: Category Scope */}
                      <div className="mt-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">
                          Category Scope
                        </label>

                        <select
                          value={formData.categoryScope}
                          onChange={(e) => {
                            const scope = e.target.value;
                            setFormData((p) => ({
                              ...p,
                              categoryScope: scope,
                              categoryIds: scope === "ALL" ? [] : p.categoryIds,
                            }));
                          }}
                          className="w-full px-3 py-2 text-xs border border-gray-300 rounded-md"
                        >
                          <option value="ALL">All Categories</option>
                          <option value="INCLUDE">Only Selected Categories</option>
                          <option value="EXCLUDE">All Except Selected Categories</option>
                        </select>

                        <p className="text-[10px] text-gray-500 mt-1">
                          INCLUDE means coupon works only for selected categories.
                          EXCLUDE means it won’t work for selected categories.
                        </p>
                      </div>

                      {/* ✅ NEW: Category Multi-select */}
                      {formData.categoryScope !== "ALL" && (
                        <div className="mt-3">
                          <label className="block text-xs font-medium text-gray-700 mb-1.5">
                            Select Categories *
                          </label>

                          <div
                            className={`border rounded-md p-2 max-h-40 overflow-y-auto ${
                              formErrors.categoryIds
                                ? "border-red-300"
                                : "border-gray-300"
                            }`}
                          >
                            {catLoading ? (
                              <div className="text-xs text-gray-500">
                                Loading categories...
                              </div>
                            ) : categories.length === 0 ? (
                              <div className="text-xs text-gray-500">
                                No categories found
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {categories.map((cat) => {
                                  const checked = (formData.categoryIds || []).includes(
                                    cat.id
                                  );
                                  return (
                                    <label
                                      key={cat.id}
                                      className="flex items-center gap-2 text-xs text-gray-700"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={(e) => {
                                          const isChecked = e.target.checked;
                                          setFormData((p) => {
                                            const prev = p.categoryIds || [];
                                            const next = isChecked
                                              ? Array.from(new Set([...prev, cat.id]))
                                              : prev.filter((x) => x !== cat.id);
                                            return { ...p, categoryIds: next };
                                          });
                                        }}
                                      />
                                      <span>{cat.name}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          {formErrors.categoryIds && (
                            <p className="text-xs text-red-600 mt-1">
                              {formErrors.categoryIds}
                            </p>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-4 border-t border-gray-200">
                    <button
                      type="submit"
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-green-600 text-white text-xs font-medium rounded-md hover:bg-green-700"
                    >
                      <Save className="w-3.5 h-3.5" />
                      <span>{editingCoupon ? "Update Coupon" : "Create Coupon"}</span>
                    </button>
                    <button
                      type="button"
                      onClick={resetForm}
                      className="flex-1 px-3 py-2 text-xs font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Coupons List */}
          {loading ? (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
              <p className="text-xs text-gray-600 mt-3">Loading coupons...</p>
            </div>
          ) : (
            <>
              <div className="mb-3 flex items-center justify-between">
                <p className="text-xs text-gray-600">
                  Showing <span className="font-medium">{filteredCoupons.length}</span> of{" "}
                  <span className="font-medium">{coupons.length}</span> coupons
                </p>

                {filteredCoupons.length > 0 && (
                  <button
                    onClick={() =>
                      copyToClipboard(filteredCoupons.map((c) => c.code).join("\n"))
                    }
                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                  >
                    <Copy className="w-3 h-3" />
                    Copy All Codes
                  </button>
                )}
              </div>

              {filteredCoupons.length === 0 ? (
                <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                  <Ticket className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm font-medium text-gray-800">No coupons found</p>
                  <p className="text-xs text-gray-600 mt-1 mb-4">
                    {searchTerm || activeFilterCount > 0
                      ? "Try adjusting your search or filters"
                      : "Get started by creating your first coupon"}
                  </p>
                  {!searchTerm && activeFilterCount === 0 && (
                    <button
                      onClick={() => setShowForm(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white text-xs font-medium rounded-md hover:bg-green-700"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>Create First Coupon</span>
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredCoupons.map((coupon) => {
                    const status = getCouponStatus(coupon);
                    const isAudienceBadge =
                      !coupon.isPatientCoupon &&
                      coupon.audience &&
                      coupon.audience !== "ALL";

                    const showCategoryBadge =
                      !coupon.isPatientCoupon &&
                      coupon.categoryScope &&
                      coupon.categoryScope !== "ALL";

                    return (
                      <div
                        key={coupon.id}
                        className="bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                      >
                        <div className="p-4 border-b border-gray-100">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-1.5">
                              <Ticket className="w-3.5 h-3.5 text-gray-400" />
                              <div>
                                <h3 className="text-sm font-mono font-semibold text-gray-800">
                                  {coupon.code}
                                </h3>

                                {coupon.isPatientCoupon ? (
                                  <div className="flex items-center gap-1 mt-0.5">
                                    <User className="w-3 h-3 text-blue-500" />
                                    <span className="text-[10px] text-blue-600">
                                      {coupon.patientId
                                        ? `Patient #${coupon.patientId}`
                                        : "Patient Coupon"}
                                    </span>
                                  </div>
                                ) : isAudienceBadge ? (
                                  <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded bg-purple-50 text-purple-700 border border-purple-200">
                                    <Users className="w-3 h-3" />
                                    {coupon.audience === "NEW_PATIENT"
                                      ? "New Patients"
                                      : "Existing Patients"}
                                  </div>
                                ) : null}

                                {/* ✅ Category scope badges */}
                                {showCategoryBadge && (
                                  <div className="mt-2 flex flex-wrap gap-1">
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded bg-gray-50 text-gray-700 border border-gray-200">
                                      <Tags className="w-3 h-3" />
                                      {coupon.categoryScope === "INCLUDE"
                                        ? "Only Categories"
                                        : "Exclude Categories"}
                                    </span>

                                    {Array.isArray(coupon.categories) &&
                                      coupon.categories.slice(0, 3).map((c) => (
                                        <span
                                          key={c.categoryId}
                                          className="inline-flex items-center px-2 py-0.5 text-[10px] rounded bg-blue-50 text-blue-700 border border-blue-200"
                                        >
                                          {c.category?.name || `#${c.categoryId}`}
                                        </span>
                                      ))}

                                    {Array.isArray(coupon.categories) &&
                                      coupon.categories.length > 3 && (
                                        <span className="text-[10px] text-gray-500">
                                          +{coupon.categories.length - 3} more
                                        </span>
                                      )}
                                  </div>
                                )}
                              </div>
                            </div>

                            <span
                              className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded ${getStatusColor(
                                status
                              )}`}
                            >
                              {getStatusIcon(status)}
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </span>
                          </div>

                          {coupon.description && (
                            <p className="text-xs text-gray-600 truncate">
                              {coupon.description}
                            </p>
                          )}
                        </div>

                        <div className="p-4">
                          <div className="space-y-3 mb-4">
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-500">Discount</span>
                              <span className="text-xs font-semibold text-green-700">
                                {coupon.discountType === "percentage"
                                  ? `${coupon.discountValue}%${
                                      coupon.maxDiscount
                                        ? ` (up to ₹${coupon.maxDiscount})`
                                        : ""
                                    }`
                                  : `₹${coupon.discountValue}`}
                              </span>
                            </div>

                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-500">Min Order</span>
                              <span className="text-xs font-medium">
                                {coupon.minOrderAmount
                                  ? `₹${coupon.minOrderAmount}`
                                  : "None"}
                              </span>
                            </div>

                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-500">Usage</span>
                              <span className="text-xs font-medium">
                                {coupon.usedCount}
                                {coupon.usageLimit ? ` / ${coupon.usageLimit}` : " / ∞"}
                                {coupon.perUserLimit &&
                                  ` (${coupon.perUserLimit} per user)`}
                              </span>
                            </div>

                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-500">Validity</span>
                              <div className="text-right">
                                {coupon.isPatientCoupon ? (
                                  <div className="text-[10px] font-medium text-gray-500">
                                    Patient coupon
                                  </div>
                                ) : coupon.validFrom || coupon.validUntil ? (
                                  <>
                                    <div className="text-[10px] font-medium">
                                      {formatDate(coupon.validFrom)}
                                    </div>
                                    <div className="text-[10px] text-gray-500">
                                      to {formatDate(coupon.validUntil)}
                                    </div>
                                  </>
                                ) : (
                                  <div className="text-[10px] font-medium text-gray-500">
                                    No expiry
                                  </div>
                                )}
                              </div>
                            </div>

                            {coupon.autoApply && (
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-gray-500">Auto Apply</span>
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-800 rounded">
                                  <CheckCircle className="w-2.5 h-2.5" />
                                  Enabled
                                </span>
                              </div>
                            )}
                          </div>

                          <div className="flex gap-2">
                            <button
                              onClick={() => canAccess(currentUser, "canEdit") && handleEdit(coupon)}
                              disabled={!canAccess(currentUser, "canEdit")}
                              className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-blue-600 border border-blue-200 rounded hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Edit2 className="w-3 h-3" />
                              <span>Edit</span>
                            </button>

                            <button
                              onClick={() => copyToClipboard(coupon.code)}
                              className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-gray-600 border border-gray-200 rounded hover:bg-gray-50"
                            >
                              <Copy className="w-3 h-3" />
                              <span>Copy</span>
                            </button>

                            <button
                              onClick={() => canAccess(currentUser, "canDelete") && handleDelete(coupon.id)}
                              disabled={!canAccess(currentUser, "canDelete")}
                              className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-red-600 border border-red-200 rounded hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Trash2 className="w-3 h-3" />
                              <span>Delete</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default CouponItem;